import { useState } from 'react'
import { useFormik } from 'formik'

import { header } from '../../constants'

import { chatOptions, enginesOptions, synapseOptions } from './types'
import { Message } from '../../components/TextChatWindow/TextChatWindow'
import { handleStreamingResponse, useHandleResponseError } from '../../utils'
import fetchToCurl from 'fetch-to-curl'

export const useVision = () => {
    const { handleResponseError } = useHandleResponseError()

    const [isLoading, setIsLoading] = useState(false)

    const [messages, setMessages] = useState<Message[]>([])
    const [images, setImages] = useState<string[]>([])

    const currentDate = new Date().toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    })

    const initialValues = {
        message: '',
        synapse_type: synapseOptions[0].value,
        streamingResponse: false,
        axon_uid: null,
        timeout: 60,
        engine: enginesOptions[0].value,
        chat_model: chatOptions[0].value,

        width: 1280,
        height: 1280,
        cfg_scale: 2,
        steps: 10,
        seed: 0,

        top_p: 1,
        temperature: 0.3,
        max_tokens: 1024,
    }

    const handleSubmit = async (values: any) => {
        try {
            const {
                message,
                synapse_type,
                // streamingResponse,
                axon_uid,
                timeout,
                engine,
                chat_model,
                width,
                height,
                cfg_scale,
                steps,
                seed,
                top_p,
                temperature,
                max_tokens,
            } = values
            setMessages((prevState: Message[]) => [
                ...prevState,
                { icon: '', message: message, date: currentDate, name: 'You' },
            ])

            if (message.trim()) {
                const query = `${
                    import.meta.env.REACT_APP_SUBNET_API
                }/s19_tau?synapse_type=${synapse_type}&streaming_response_enabled=true&timeout=${timeout}${
                    synapse_type === 'TextToImage' ? `&engine=${engine}` : ''
                }`

                const options = {
                    method: 'POST',
                    headers: header,
                    body: JSON.stringify({
                        TextToImage: {
                            text_prompts: [
                                {
                                    text: message,
                                    weight: 1,
                                },
                            ],
                            width,
                            height,
                            cfg_scale,
                            steps,
                            seed,
                        },
                        ImageToImage: {
                            seed,
                            steps,
                            cfg_scale,
                            init_image: 'image_b64',
                            text_prompts: [
                                {
                                    text: message,
                                    weight: 1,
                                },
                            ],
                            image_strength: '0.5',
                        },
                        Avatar: {
                            text_prompts: [
                                {
                                    text: message,
                                    weight: 1,
                                },
                            ],
                            ipadapter_strength: 0.5,
                            control_strength: 0.5,
                            height,
                            width,
                            steps,
                            seed,
                            init_image: 'image_b64',
                        },
                        Inpaint: {
                            seed,
                            steps,
                            cfg_scale,
                            init_image: 'image_b64',
                            mask_image: 'image_b64',
                            text_prompts: [
                                {
                                    text: message,
                                    weight: 1,
                                },
                            ],
                        },
                        ClipEmbeddings: {
                            image_b64s: ['image_b64', 'image_b64'],
                        },
                        Upscale: {
                            image: 'image_b64',
                        },
                        Chat: {
                            messages: [
                                {
                                    role: 'user',
                                    content: message,
                                },
                            ],
                            top_p,
                            temperature,
                            max_tokens,
                            seed,
                            model: chat_model,
                        },
                    }),
                }

                const curlString = fetchToCurl(query, options)?.replace(/\$/g, '\\$')

                const response: any = await fetch(query, options)

                handleResponseError(response)

                if (!response.body) return

                if (synapse_type === 'Chat') {
                    handleStreamingResponse(response, (text: string) => {
                        console.log('text', text)
                    })
                } else {
                    const data = await response.json()
                    console.log('data', data)
                    if (!response.ok) {
                        throw new Error(data.detail.map((d: { msg: string }) => d.msg).join(', '))
                    }

                    if (synapse_type === 'TextToImage') {
                        const imageUrl = `data:image/jpeg;base64,${data?.response?.[0]?.image_b64}`
                        setImages((prevState: string[]) => [imageUrl, ...prevState])
                    }
                }
            }
        } catch (e) {
            console.log(e)
        }
    }

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: async (values, formikHelpers) => {
            formikHelpers.setFieldValue('message', '') // Reset message to empty string immediately
            setIsLoading(true)
            await handleSubmit(values)
            setIsLoading(false)
        },
    })

    return {
        formik,
        responseLoading: isLoading,
        messages,
        images,
    }
}
